// src/intervenciones/FinalizarIntervencion.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Global } from "../helpers/Global";
import { ticajeStore } from "../data/ticajeStore";

// ===== MUI =====
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";

export const FinalizarIntervencion = ({
  ticaje,
  onTicajeActualizado,
  onSuccess,
  onError,
  uiLocked = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [centroCoords, setCentroCoords] = useState(null); // { lat, lon }
  const [centroNombre, setCentroNombre] = useState("");
  const limpiarMensajeTimeout = useRef(null);
  const clickedRef = useRef(false); // anti-doble click

  // --- Utils ---
  const flash = (msg, ms = 4500) => {
    setMensaje(msg);
    if (limpiarMensajeTimeout.current) clearTimeout(limpiarMensajeTimeout.current);
    limpiarMensajeTimeout.current = setTimeout(() => setMensaje(""), ms);
  };

  // Haversine -> metros
  const distanciaMetros = (lat1, lon1, lat2, lon2) => {
    const R = 6371000; // m
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  useEffect(() => {
    return () => {
      if (limpiarMensajeTimeout.current) clearTimeout(limpiarMensajeTimeout.current);
    };
  }, []);

  // Normaliza campos (acepta distintas formas que pueden llegar desde la API)
  const info = useMemo(() => {
    if (!ticaje) return null;

    const latCentro =
      ticaje?.centro?.latitud ??
      ticaje?.centro_latitud ??
      ticaje?.latitud_centro ??
      ticaje?.centroLat;

    const lonCentro =
      ticaje?.centro?.longitud ??
      ticaje?.centro_longitud ??
      ticaje?.longitud_centro ??
      ticaje?.centroLon;

    return {
      id: ticaje.id,
      esLocal: String(ticaje.id || "").startsWith("local"),
      cliente:
        ticaje?.cliente?.nombre ??
        ticaje?.cliente_nombre ??
        ticaje?.cliente ??
        "",
      centroId: ticaje?.centro_id ?? ticaje?.centroId ?? ticaje?.centro?.id ?? null,
      centro: ticaje?.centro?.nombre ?? ticaje?.centro_nombre ?? ticaje?.centro ?? "",
      ordenId: ticaje?.orden_id ?? ticaje?.ordenId ?? ticaje?.orden?.id ?? null,
      inicio: ticaje?.inicio ?? ticaje?.fecha_inicio ?? ticaje?.created_at ?? null,
      latCentro: latCentro != null ? Number(latCentro) : null,
      lonCentro: lonCentro != null ? Number(lonCentro) : null,
    };
  }, [ticaje]);

  // Cargar coords y nombre de centro si no vienen dentro del objeto
  useEffect(() => {
    let alive = true;

    const loadCentro = async () => {
      if (!info?.id) return;

      // Si ya tenemos coords embebidas, úsalas
      if (info.latCentro != null && info.lonCentro != null) {
        if (!alive) return;
        setCentroCoords({ lat: info.latCentro, lon: info.lonCentro });
        if (info.centro && info.centro.trim() !== "") {
          setCentroNombre(info.centro);
        }
        return;
      }

      // Si no tenemos coords pero sí ordenId + centroId, intentamos resolver
      if (!info.ordenId || !info.centroId) return;

      try {
        const listaCentros = await ticajeStore
          .getCentrosPorOrden?.(info.ordenId)
          .catch(() => []);

        if (!alive) return;

        const centrosArr = Array.isArray(listaCentros)
          ? listaCentros
          : listaCentros?.data || [];

        const c = centrosArr.find((z) => Number(z.id) === Number(info.centroId));

        if (c) {
          if (c.latitud != null && c.longitud != null) {
            setCentroCoords({ lat: Number(c.latitud), lon: Number(c.longitud) });
          } else {
            flash("Este centro no tiene coordenadas asignadas.");
          }
          if (c.nombre && !info.centro) {
            setCentroNombre(c.nombre);
          }
        }
      } catch (e) {
        if (!alive) return;
        console.warn("[FinalizarIntervencion] getCentrosPorOrden:", e?.message);
        // Si falla en offline/online, podremos cerrar sin validar distancia
      }
    };

    loadCentro();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info?.id, info?.centroId, info?.ordenId, info?.latCentro, info?.lonCentro, info?.centro]);

  // Nombre centro a mostrar
  const displayCentro =
    info?.centro && info.centro.trim() !== ""
      ? info.centro
      : centroNombre || (info?.centroId ? `Centro ${info.centroId}` : "Centro no disponible");

  // --- Cierre con geolocalización y payload extendido ---
  const finalizarConUbicacion = async () => {
    if (clickedRef.current || uiLocked) return;
    clickedRef.current = true;

    if (!navigator.geolocation) {
      clickedRef.current = false;
      onError?.("Tu navegador no soporta geolocalización.");
      return;
    }

    setLoading(true);

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const latFin = pos.coords.latitude;
          const lonFin = pos.coords.longitude;

          let metros = 0;
          let enRango = true;

          if (centroCoords?.lat != null && centroCoords?.lon != null) {
            metros = distanciaMetros(latFin, lonFin, centroCoords.lat, centroCoords.lon);
            const UMBRAL_M = Number(Global?.rango_metros ?? 100);
            enRango = metros <= UMBRAL_M;

            const km = metros / 1000;
            const distanciaKm = new Intl.NumberFormat("es-ES", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(km);

            if (!enRango) {
              const ok = window.confirm(
                `Estás a ${distanciaKm} km del centro. ¿Deseas finalizar igualmente?`
              );
              if (!ok) {
                setLoading(false);
                clickedRef.current = false;
                return;
              }
            }
          }

          // ✅ Delega en la store: decide si es intervención local u online y resuelve endpoints/outbox
          const result = await ticajeStore.finish({
            ticaje: info,
            latitud: latFin,
            longitud: lonFin,
            enRango,
            metros,
          });

          if (result?.status === "online") {
            onSuccess?.("Intervención finalizada correctamente.");
          } else if (result?.status === "offline") {
            onSuccess?.("Sin conexión: cierre guardado y se enviará automáticamente.");
          } else if (result?.status === "noop") {
            onError?.("No hay intervención activa que finalizar.");
          } else {
            onSuccess?.("Intervención finalizada.");
          }

          await onTicajeActualizado?.();
        } catch (e) {
          console.error(e);
          onError?.("No se pudo finalizar la intervención.");
        } finally {
          setLoading(false);
          clickedRef.current = false;
        }
      },
      (err) => {
        console.error(err);
        onError?.("No se pudo obtener la ubicación al cerrar la intervención.");
        setLoading(false);
        clickedRef.current = false;
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Cancelación (online/offline a través de la store)
  const cancelarIntervencion = async () => {
    if (clickedRef.current || uiLocked) return;
    clickedRef.current = true;

    try {
      setLoading(true);
      const result = await ticajeStore.cancel({ ticaje: info });

      if (result?.status === "online") {
        onSuccess?.("Intervención cancelada correctamente.");
      } else if (result?.status === "offline") {
        onSuccess?.("Sin conexión: cancelación guardada y se enviará automáticamente.");
      } else if (result?.status === "noop") {
        onError?.("No hay intervención activa que cancelar.");
      } else {
        onSuccess?.("Intervención cancelada.");
      }

      await onTicajeActualizado?.();
    } catch (e) {
      console.error(e);
      onError?.("No se pudo cancelar la intervención.");
    } finally {
      setLoading(false);
      clickedRef.current = false;
    }
  };

  const botonDeshabilitado = uiLocked || loading;

  // =========================
  // Render
  // =========================
  return (
    <Box className="appContainer">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Intervención en curso
        </Typography>

        {mensaje && (
          <Typography color="error" sx={{ fontSize: 14 }}>
            {mensaje}
          </Typography>
        )}

        {/* ✅ MISMO patrón que FinalizarJornada */}
        <Box className="contentRowCenter">
          <Box className="contentContainer contentContainer--compactTable">
            <Paper sx={{ p: 2 }}>
              {!info?.id ? (
                <Typography>No hay ninguna intervención activa.</Typography>
              ) : (
                <Table size="small" sx={{ width: "100%" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell><b>Cliente</b></TableCell>
                      <TableCell><b>Centro</b></TableCell>
                      <TableCell><b>Inicio</b></TableCell>
                      <TableCell align="right"><b>Acciones</b></TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    <TableRow>
                      <TableCell>{info.cliente || "Cliente no disponible"}</TableCell>
                      <TableCell>{displayCentro}</TableCell>
                      <TableCell>
                        {info.inicio
                          ? new Date(info.inicio).toLocaleString()
                          : "Fecha no disponible"}
                      </TableCell>
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={1.5}
                          sx={{ justifyContent: "flex-end", flexWrap: "wrap" }}
                        >
                          <Button onClick={finalizarConUbicacion} disabled={botonDeshabilitado}>
                            {loading ? "Procesando..." : "Finalizar"}
                          </Button>

                          <Button
                            variant="outlined"
                            onClick={cancelarIntervencion}
                            disabled={botonDeshabilitado}
                          >
                            Cancelar
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </Paper>
          </Box>
        </Box>
      </Stack>
    </Box>
  );

};
